import React, { useState, useEffect } from 'react';
import { mfaApi } from '../../api/mfa';
import './MFASetupRequired.css';

interface MFASetupRequiredProps {
  sessionId: string;
  userInfo: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

const MFASetupRequired: React.FC<MFASetupRequiredProps> = ({ sessionId, userInfo }) => {
  const [step, setStep] = useState<'loading' | 'qr' | 'verify' | 'backup'>('loading');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    startSetup();
  }, []);

  const startSetup = async () => {
    try {
      setStep('loading');
      setError('');
      const response = await mfaApi.post('/auth/mfa/forced-setup', {
        session_id: sessionId
      });
      const data = response.data as any;
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep('qr');
    } catch (err: any) {
      setError('Failed to initialize MFA setup. Please refresh and try again.');
      console.error('MFA setup error:', err);
    }
  };

  const verifySetup = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await mfaApi.post('/auth/mfa/forced-verify-setup', {
        session_id: sessionId,
        token: verificationCode
      });
      
      const data = response.data as any;
      
      // Store the auth token
      localStorage.setItem('token', data.token);
      
      setBackupCodes(data.backup_codes);
      setStep('backup');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = `PCP Claims System - Backup Codes
Generated: ${new Date().toLocaleString()}

IMPORTANT: Store these codes in a safe place. Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Use these codes if you lose access to your authenticator app.`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pcp-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    // Redirect to the main app
    window.location.href = '/';
  };

  return (
    <div className="mfa-setup-required-container">
      <div className="mfa-setup-required-content">
        <div className="mfa-header">
          <h2>Two-Factor Authentication Required</h2>
          <p className="subtitle">
            Your administrator requires two-factor authentication for your account.
          </p>
        </div>

        <div className="mfa-body">
          {step === 'loading' && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Initializing MFA setup...</p>
            </div>
          )}

          {step === 'qr' && (
            <div className="mfa-step-qr">
              <h3>Step 1: Scan QR Code</h3>
              <p>Scan this QR code with your authenticator app:</p>
              
              <div className="qr-section">
                <img src={qrCode} alt="MFA QR Code" className="qr-code" />
                
                <div className="app-suggestions">
                  <p>Recommended apps:</p>
                  <ul>
                    <li>Google Authenticator</li>
                    <li>Microsoft Authenticator</li>
                    <li>Authy</li>
                    <li>1Password</li>
                  </ul>
                </div>
              </div>
              
              <div className="manual-entry">
                <p>Can't scan? Enter this code manually:</p>
                <code className="secret-code">{secret}</code>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={() => setStep('verify')}
              >
                Next: Verify Code
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div className="mfa-step-verify">
              <h3>Step 2: Verify Setup</h3>
              <p>Enter the 6-digit code from your authenticator app:</p>
              
              <input
                type="text"
                className="verification-input"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                maxLength={6}
                autoFocus
              />
              
              {error && (
                <div className="error-message">{error}</div>
              )}
              
              <div className="button-group">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setStep('qr')}
                  disabled={loading}
                >
                  Back
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={verifySetup}
                  disabled={verificationCode.length !== 6 || loading}
                >
                  {loading ? 'Verifying...' : 'Verify and Enable MFA'}
                </button>
              </div>
            </div>
          )}

          {step === 'backup' && (
            <div className="mfa-step-backup">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>MFA Enabled Successfully!</h3>
                <p>Your account is now protected with two-factor authentication.</p>
              </div>
              
              <div className="backup-codes-section">
                <h4>Backup Codes</h4>
                <p className="warning-text">
                  ⚠️ IMPORTANT: Save these backup codes now. You won't be able to see them again.
                </p>
                
                <div className="backup-codes-list">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code">
                      <span className="code-number">{index + 1}.</span>
                      <code>{code}</code>
                    </div>
                  ))}
                </div>
                
                <button 
                  className="btn btn-secondary"
                  onClick={downloadBackupCodes}
                >
                  Download Backup Codes
                </button>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={handleComplete}
              >
                Continue to Application
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFASetupRequired;